import { FC, ReactNode, useContext } from 'react';
import styled from 'styled-components';
import { ProductsContext } from 'context-providers/products-provider';
import { formatMoney } from 'lib/utils/format-money';
import {
  ContentContainer,
  ProductInfoContainer,
  ProductHeaderContainer,
  ProductPriceContainer,
  DiscountLabelContainer,
  PriceDetailsContainer,
  CrossedOutPrice,
  ActionContainer,
} from './styles';
import { TypeBody, TypeBodyLarge, TypeHeadingQuarternary } from '../Typography';
import Button from '../Button';
import Label from '../Label';
import Pill from 'components/global/Pill';
import TextButton from '../TextButton';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { ActionObject } from 'lib/product-actions';
import Image from 'next/image';

const ProductTitleSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ProductTitle = styled(TypeHeadingQuarternary)`
  /* Mobile styles */
  --fz: 1.25rem; /* 20px */
  --fw: 350;
  --lh: 1.4; /* 140% = 28px */
  --lts: -0.2px;
  font-style: normal;

  @media (min-width: 375px) {
    white-space: nowrap;
  }

  /* Desktop styles */
  @media (min-width: 768px) {
    --fz: 1.5rem; /* 24px */
    --fw: 350;
    --lh: 1.2; /* 120% = 28.8px */
    --lts: -0.36px;
  }
`;

const ProductDescription = styled(TypeBody)`
  /* Mobile styles */
  --fz: 0.875rem; /* 14px */
  --fw: 350;
  --lh: 1.4; /* 140% = 19.6px */
  --lts: -0.07px;
  font-style: normal;

  /* Desktop styles */
  @media (min-width: 768px) {
    --fz: 1rem; /* 16px */
    --fw: 350;
    --lh: 1.4; /* 140% = 22.4px */
    --lts: -0.16px;
  }
`;

const GridProductPrice = styled(TypeBodyLarge)`
  /* Mobile styles */
  --fz: 1rem; /* 16px */
  --fw: 350;
  --lh: 1.4; /* 140% = 22.4px */
  --lts: -0.16px;
  font-style: normal;

  /* Desktop styles */
  @media (min-width: 768px) {
    --fz: 1.25rem; /* 20px */
    --fw: 350;
    --lh: 1.3; /* 130% = 26px */
    --lts: -0.2px;
  }
`;

const FeaturedProductPrice = styled(TypeBodyLarge)`
  /* Same for both mobile and desktop */
  --fz: 1.25rem; /* 20px */
  --fw: 350;
  --lh: 1.3; /* 130% = 26px */
  --lts: -0.2px;
  font-style: normal;
`;

export interface ProductContentContainerProps {
  // Content props
  id: string;
  title?: string;
  description?: string | ReactNode;
  isFeatured?: boolean;

  badges?: Array<{
    text: string;
    trademark?: boolean;
    registered?: boolean;
    color?: 'seedgreen' | 'snowwhite' | 'amprimary' | 'pmprimary' | 'dmprimary';
    type?: 'pill' | 'label';
  }>;

  // Price props
  price?: string;
  salePrice?: string | null;
  showDiscount?: boolean;
  discountText?: string;

  styleConfig?: {
    flexGrow?: number;
    flexShrink?: number;
    flexBasis?: string;
    height?: string;
    width?: string;
    padding?: string;

    // Theme props
    textColor?: 'snowwhite' | 'seedgreen';

    // Action container config
    actionConfig?: {
      gap?: string;
      alignItems?: string;
      flexWrap?: string;
      buttonWidth?: string;
      buttonFlexGrow?: number;
    };
    // Price container config
    priceConfig?: {
      gap?: string;
    };
  };

  // Action props
  primaryAction?: ActionObject | null;
  secondaryAction?: ActionObject | null;

  // Custom content
  className?: string;
  numberOneProbioticImage?: string;
  numberOneProbioticAltText?: string;
  numberOneProbioticDisclaimer?: string;
  numberOneProbioticDisclaimerMobile?: string;
}

const ProductContentContainer: FC<ProductContentContainerProps> = ({
  id,
  title,
  description,
  numberOneProbioticImage,
  numberOneProbioticAltText,
  numberOneProbioticDisclaimer,
  numberOneProbioticDisclaimerMobile,
  badges = [],
  price,
  salePrice,
  showDiscount = false,
  discountText,
  primaryAction,
  secondaryAction,
  className,
  styleConfig,
  isFeatured = false,
}) => {
  const isSmall = useClientSideMediaQuery(1024);
  const { currencyCode } = useContext(ProductsContext);
  const displayedPrice = salePrice || price;
  const isPrimaryActionDisabled =
    primaryAction?.disabled ??
    (!!primaryAction && !primaryAction.url && !primaryAction.onClick);

  const renderedBadges = badges.map((badge, index) => {
    if (badge.type === 'label') {
      return (
        <Label
          key={`${badge.text}-${badge.type}-${index}`}
          size='small'
          color={badge.color || 'seedgreen'}
        >
          {badge.text}
        </Label>
      );
    }

    return (
      <Pill
        className='product-pill'
        key={`${badge.text}-${badge.type}-${index}`}
        light={badge.color === 'snowwhite'}
        trademark={badge.trademark}
        registered={badge.registered}
        size='xs'
        padding='6px'
      >
        {badge.text}
      </Pill>
    );
  });

  const renderDiscountLabel = (text: string) => (
    <Label size='small' color='seedgreen'>
      {text}
    </Label>
  );

  const headerBadges = renderedBadges;

  return (
    <ContentContainer
      id={id}
      className={className}
      width={styleConfig?.width}
      height={styleConfig?.height}
      flexGrow={styleConfig?.flexGrow}
      flexShrink={styleConfig?.flexShrink}
      flexBasis={styleConfig?.flexBasis}
      padding={styleConfig?.padding}
      config={{
        textColor: styleConfig?.textColor,
        actionConfig: styleConfig?.actionConfig,
        priceConfig: styleConfig?.priceConfig,
      }}
    >
      <ProductInfoContainer id='product-info-container'>
        <ProductHeaderContainer id='product-header-container'>
          {headerBadges}
          {title && (
            <ProductTitleSection>
              <ProductTitle color={styleConfig?.textColor} id='product-title'>
                {title}
              </ProductTitle>
              {isFeatured && isSmall ? (
                <FeaturedProductPrice color={styleConfig?.textColor}>
                  {formatMoney(displayedPrice, currencyCode)}
                </FeaturedProductPrice>
              ) : null}
            </ProductTitleSection>
          )}
        </ProductHeaderContainer>
        {numberOneProbioticImage && (
          <div>
            <Image
              src={numberOneProbioticImage}
              alt={numberOneProbioticAltText || 'Number One Probiotic'}
              width={127}
              height={35}
            />
          </div>
        )}
        {description && (
          <ProductDescription
            id='product-description'
            color={styleConfig?.textColor}
          >
            {description}
          </ProductDescription>
        )}
      </ProductInfoContainer>

      {price && (!isFeatured || !isSmall) /* TODO: fix this logic */ && (
        <ProductPriceContainer id='product-price-container'>
          {showDiscount && discountText && (
            <DiscountLabelContainer>
              {renderDiscountLabel(discountText)}
            </DiscountLabelContainer>
          )}
          <PriceDetailsContainer
            id='product-price-details-container-main'
            config={styleConfig?.priceConfig}
          >
            {isFeatured ? (
              <FeaturedProductPrice color={styleConfig?.textColor}>
                {formatMoney(displayedPrice, currencyCode)}
              </FeaturedProductPrice>
            ) : (
              <GridProductPrice color={styleConfig?.textColor}>
                {formatMoney(displayedPrice, currencyCode)}
              </GridProductPrice>
            )}
            {salePrice ? (
              <CrossedOutPrice>
                {formatMoney(price, currencyCode)}
              </CrossedOutPrice>
            ) : null}
          </PriceDetailsContainer>
        </ProductPriceContainer>
      )}

      {(primaryAction || secondaryAction) && (
        <ActionContainer
          id='product-action-container'
          config={styleConfig?.actionConfig}
          hasBothActions={!!(primaryAction && secondaryAction)}
        >
          {primaryAction && (
            <Button
              id={primaryAction.id || 'add-to-cart-button'}
              color={styleConfig?.textColor || 'seedgreen'}
              size='large'
              label={primaryAction.label}
              url={primaryAction.url}
              onClick={primaryAction.onClick}
              disabled={isPrimaryActionDisabled}
            />
          )}
          {secondaryAction && (
            <TextButton
              id={'learn-more-button'}
              label={secondaryAction.label}
              color={styleConfig?.textColor || 'seedgreen'}
              underline={true}
              hideArrow={true}
              url={secondaryAction.url}
              onClick={secondaryAction.onClick}
              transform='lower'
            />
          )}
        </ActionContainer>
      )}
      {numberOneProbioticDisclaimer && (
        // @ts-ignore
        <TypeBody
          as='div'
          style={{
            fontSize: '10px',
            lineHeight: '14px',
            letterSpacing: '-.05px',
            fontWeight: 350,
            color: 'rgba(252, 252, 247, 0.5)',
          }}
          dangerouslySetInnerHTML={{
            __html: isSmall
              ? numberOneProbioticDisclaimerMobile
              : numberOneProbioticDisclaimer,
          }}
          css={`
            sup {
              color: rgba(252, 252, 247, 0.5);
            }
          `}
        />
      )}
    </ContentContainer>
  );
};

export default ProductContentContainer;
